import { put, takeLatest, all, call, select } from 'redux-saga/effects'

import {
    actions,
    getSubscriptionPaymentMethodsSuccess,
    getSubscriptionPaymentMethodsFailed,
    getSubscriptionPlanDetailsSuccess,
    getSubscriptionPlanDetailsFailed,
    signupNewSubscriptionFailed,
    signupNewSubscriptionSuccess,
    updateSubscriptionPaymentSuccess,
    updateSubscriptionPaymentFailed,
    getSubscriptionRenewalsSuccess,
    getSubscriptionRenewalsFailed,
    cancelSubscriptionSuccess,
    cancelSubscriptionFailed,
    getSubscriptionPromoProductsSuccess,
    getSubscriptionPromoProductsFailed,
    reactivateSubscriptionSuccess,
    reactivateSubscriptionFailed,
    failedSubscriptionPayment,
    setShowFailedSubscriptionPaymentModal,
} from './actions'
import { api } from '../../network/api'
import {
    subscriptionPlanDetailsURL,
    signupSubscriptionURL,
    updateSubscriptionURL,
    getSubscriptionRenewalsURL,
    subscriptionPromoProductsURL,
} from './urls'
import { getCardsURL } from 'modules/wallet/urls'
import { util } from 'utils'
import { request } from 'utils/requestModel'
import { selectSubscriptionCard } from 'modules/subscription/actions'
import { addSubscriptionSuperProp } from 'middleware/helper'
import { setLastSubscriptionRenewalDate } from 'modules/wallet/actions'

export function* getSubscriptionPlanDetails(action) {
    const { payload } = action
    let url = subscriptionPlanDetailsURL.replace('{sub_id}', payload)
    try {
        const response = yield call(api, url, 'GET', request, 0)
        yield put(getSubscriptionPlanDetailsSuccess(response))
        if (response) {
            addSubscriptionSuperProp(response)
        }
    } catch (error) {
        yield put(getSubscriptionPlanDetailsFailed(error))
    }
}

export function* getSubscriptionPaymentMethods(action) {
    try {
        const { user } = yield select()
        const profile = user.get('userProfile')
        const training_mode = (profile && profile.get('training_mode')) || false
        let timeStamp = new Date().getTime()
        const paymentMethods = yield call(
            api,
            `${getCardsURL}/?live=${!training_mode}&subscriptions=true&timeStamp=${timeStamp}`,
            'GET'
        )
        yield put(getSubscriptionPaymentMethodsSuccess(paymentMethods.payment_methods))
    } catch (error) {
        yield put(getSubscriptionPaymentMethodsFailed(error))
    }
}

export function* cancelSubscription(action) {
    const { payload } = action
    let url = subscriptionPlanDetailsURL.replace('{sub_id}', payload)
    const reqBody = {
        action: 'cancel',
    }
    try {
        const response = yield call(api, url, 'POST', reqBody)
        if (response?.hasOwnProperty('action_status') && response?.action_status === 'success') {
            yield put(cancelSubscriptionSuccess(response))
        } else {
            yield put(cancelSubscriptionFailed(response))
            throw new Error('subscription cancelation error ')
        }
    } catch (error) {
        yield put(cancelSubscriptionFailed({ ...error, action_status: 'failed' }))
    }
}

export function* signupNewSubscription(action) {
    const { payload } = action
    const { subscription } = yield select()
    const url = signupSubscriptionURL
    const card = payload.payment_details
    const amount = payload.amount
    const request = {
        sub_id: payload.sub_id,
        billing_address: payload.billing_address,
        payment_details: {
            payment_mode: 'card', // will expand logic when additional payments are accepted for subscriptions
            // source: 'token', // applicable for g-pay, apple-pay
            gateway: card.gateway,
            payment_customer_id: card.customer,
            payment_method_id: card.id,
            brand: card.brand,
            card: card.last4,
            exp_month: card.exp_month,
            exp_year: card.exp_year,
            // todo check amount with tax
            amount: amount,
        },
    }
    try {
        const response = yield call(api, url, 'POST', request)
        if (response?.hasOwnProperty('action_status') && response?.action_status === 'success') {
            yield put(signupNewSubscriptionSuccess(response))
        } else {
            yield put(signupNewSubscriptionFailed(response))
            throw new Error('subscription signup error ')
        }
    } catch (error) {
        console.log(error)
        yield put(signupNewSubscriptionFailed({ ...error, action_status: 'failed' }))
    }
}

export function* updateSubscriptionPayment(action) {
    const { payload } = action
    const { subscription } = yield select()
    const amount = subscription.subscriptionPlanDetails?.amount
    const payment_details = {
        payment_method_id: payload.payment_details.id,
        payment_customer_id: payload.payment_details.customer,
        brand: payload.payment_details.brand,
        card: payload.payment_details.last4,
        exp_month: payload.payment_details.exp_month,
        exp_year: payload.payment_details.exp_year,
        gateway: payload.payment_details.gateway,
        payment_mode: 'card',
        amount: amount,
    }
    const request = {
        action: 'update',
        payment_details: payment_details,
    }
    const newSubscription = payload.newSubscription
    const is_active = subscription.subscriptionPlanDetails?.is_active

    const url = `${updateSubscriptionURL}/${payload.sub_id}`
    if (is_active && !newSubscription) {
        try {
            const response = yield call(api, url, 'POST', request)
            if (
                response?.hasOwnProperty('action_status') &&
                response?.action_status === 'success'
            ) {
                yield put(updateSubscriptionPaymentSuccess({ ...response, case: 'updatePayment' }))
                yield put(selectSubscriptionCard(payload.payment_details))
            } else if (response?.action_status === 'failed') {
                yield put(updateSubscriptionPaymentFailed({ ...response, case: 'updatePayment' }))
            }
        } catch (error) {
            yield put(updateSubscriptionPaymentFailed({ ...error, action_status: 'failed' }))
            throw new Error('update subscription payment error ')
        }
    } else {
        const card = payload.payment_details
        yield put(selectSubscriptionCard(card))
        yield put(updateSubscriptionPaymentSuccess())
    }
}

export function* reactivateSubscription(action) {
    const { payload } = action
    const { subscription } = yield select()
    let url = subscriptionPlanDetailsURL.replace('{sub_id}', payload.sub_id)
    const card = payload.payment_details
    const amount = subscription.subscriptionPlanDetails?.amount
    const request = {
        action: 'reactivate',
        payment_details: {
            payment_mode: 'card',
            gateway: card.gateway,
            payment_customer_id: card.customer,
            payment_method_id: card.id,
            brand: card.brand,
            card: card.last4,
            exp_month: card.exp_month,
            exp_year: card.exp_year,
            amount: amount,
        },
    }
    try {
        const response = yield call(api, url, 'POST', request)
        if (response?.hasOwnProperty('action_status')) {
            yield put(reactivateSubscriptionSuccess(response))
        } else {
            throw new Error('subscription reactivate error ')
        }
    } catch (error) {
        yield put(reactivateSubscriptionFailed({ ...error, action_status: 'failed' }))
    }
}

export function* getSubscriptionRenewals(action) {
    const { payload } = action
    let url = getSubscriptionRenewalsURL.replace('{sub_id}', payload)
    try {
        const response = yield call(api, url, 'GET')
        const parsedResponse = util.parseRenewalsResponse(response)
        yield put(getSubscriptionRenewalsSuccess(parsedResponse))
    } catch (error) {
        yield put(getSubscriptionRenewalsFailed(error))
    }
}

export function* getSubscriptionPromoProducts(action) {
    const { payload } = action
    const { location, basket } = yield select()
    const currentStoreId = location.get('selectedStore').toJS().id
    const sub_id = basket.get('subscription_id')
    const itemsInbasket = basket
        .get('basketItems')
        .toJS()
        .map(item => {
            return {
                product_id: item.product_id,
                quantity: item.quantity,
            }
        })
    const request = {
        store_id: currentStoreId,
        sub_id: sub_id,
        items: itemsInbasket,
    }
    let url = subscriptionPromoProductsURL
    try {
        const response = yield call(api, url, 'POST', request)
        const items = response.items
        yield put(getSubscriptionPromoProductsSuccess(items))
    } catch (error) {
        yield put(getSubscriptionPromoProductsFailed())
    }
}

export function* subscriptionPaymentStatus() {
    const { user, wallet } = yield select()

    const userProfile = user.toJS().userProfile
    const lastGoldPassRenewalDate = wallet.toJS().lastSubscriptionRenewalDate
    const currentGoldPassRenewalDate = userProfile?.subscriptions
        ? userProfile?.subscriptions[0]?.last_renewal_date
        : null

    const failedPaymentStatus = () => {
        const subscriptionStatus = userProfile?.subscriptions
            ? userProfile?.subscriptions[0]?.status
            : null
        const cancelReason = userProfile?.subscriptions
            ? userProfile?.subscriptions[0]?.cancel_reason
            : null
        const resubscribe_allowed = userProfile.subscriptions[0]?.resubscribe_allowed === true

        return (
            resubscribe_allowed &&
            (subscriptionStatus.toLowerCase() === 'canceled' || 'suspended') &&
            cancelReason.toLowerCase() === 'payment failure'
        )
    }

    const showFailedPaymentModal = () => {
        if (lastGoldPassRenewalDate === currentGoldPassRenewalDate) return false
        else return true
    }

    if (showFailedPaymentModal())
        yield put(setLastSubscriptionRenewalDate(currentGoldPassRenewalDate))

    yield put(failedSubscriptionPayment(failedPaymentStatus()))
    if (failedPaymentStatus())
        yield put(setShowFailedSubscriptionPaymentModal(showFailedPaymentModal()))
}

function* getSubscriptionPlanDetailsWatcher() {
    yield takeLatest(actions.GET_SUBSCRIPTION_PLAN_DETAILS, getSubscriptionPlanDetails)
}

function* getSubscriptionPaymentMethodsWatcher() {
    yield takeLatest(actions.GET_SUBSCRIPTION_PAYMENT_METHODS, getSubscriptionPaymentMethods)
}

function* cancelSubscriptionWatcher() {
    yield takeLatest(actions.CANCEL_SUBSCRIPTION, cancelSubscription)
}

function* signupNewSubscriptionWatcher() {
    yield takeLatest(actions.SIGNUP_NEW_SUBSCRIPTION, signupNewSubscription)
}

function* updateSubscriptionPaymentWatcher() {
    yield takeLatest(actions.UPDATE_SUBSCRIPTION_PAYMENT, updateSubscriptionPayment)
}

function* getSubscriptionRenewalsWatcher() {
    yield takeLatest(actions.GET_SUBSCRIPTION_RENEWALS, getSubscriptionRenewals)
}

function* getSubscriptionPromoProductsWatcher() {
    yield takeLatest(actions.GET_SUBSCRIPTION_PROMO_PRODUCTS, getSubscriptionPromoProducts)
}

function* reactivateSubscriptionWatcher() {
    yield takeLatest(actions.REACTIVATE_SUBSCRIPTION, reactivateSubscription)
}

export default function* subscriptionsSaga() {
    yield all([
        getSubscriptionPlanDetailsWatcher(),
        getSubscriptionPaymentMethodsWatcher(),
        signupNewSubscriptionWatcher(),
        updateSubscriptionPaymentWatcher(),
        getSubscriptionRenewalsWatcher(),
        cancelSubscriptionWatcher(),
        getSubscriptionPromoProductsWatcher(),
        reactivateSubscriptionWatcher(),
    ])
}
